import './ChaseSequenceCard.css';
import { DispatchCardDismiss } from './DispatchCardDismiss';

/**
 * V9.2.1 (2026-06-06) — ChaseSequenceCard.
 *
 * Pure-presentation render of a `force_chase_resolve` dispatch action.
 * The LLM picks the maneuver + bid + current range; this card surfaces
 * the decision the GM sees BEFORE the bid roll is resolved on the
 * frontend. Resolution (Pursue/Flee chart lookup, mishap roll, range
 * shift) is downstream — this is the SURFACE.
 *
 * Schema 1-to-1 with `force_chase_resolve` tool args in
 * briefing_room_backend/routes/dispatch.py:349-405:
 *   - maneuver: pursue | flee | force | quick_turn | double_back | trick
 *   - bid_ease_factor: 1-10 (low bid = more daring, lower than safety EF
 *     triggers a mishap roll on failure)
 *   - current_range: close | medium | long | distant | extreme
 *   - rationale: GM voice-line ≤30 words (optional)
 *
 * Safety Ease Factors per seed/tables/chase_maneuvers.json:
 *   Pursue/Flee=6, Force=4, Quick Turn=4, Double Back=4, Trick=3.
 *
 * Engine note: the tool schema splits `pursue` and `flee` into two
 * maneuvers but the canonical engine (engine/chases.py) collapses them
 * to a single `pursue_flee` id with an is_pursue boolean. The card
 * preserves the tool-schema split for clarity (the GM sees PURSUING vs
 * FLEEING distinctly).
 */

export type ChaseManeuver =
  | 'pursue'
  | 'flee'
  | 'force'
  | 'quick_turn'
  | 'double_back'
  | 'trick';

export type ChaseRange = 'close' | 'medium' | 'long' | 'distant' | 'extreme';

export interface ChaseSequenceCardProps {
  maneuver: ChaseManeuver;
  bidEaseFactor: number;
  currentRange: ChaseRange;
  rationale?: string;
  /** Hero points the PC has available; when >0 surfaces a spend hint. */
  heroPointsAvailable?: number;
  /** V9.2.x.3: when provided, renders a ✕ dismiss button (top-right). */
  onDismiss?: () => void;
  testidPrefix?: string;
}

const RANGE_ORDER: readonly ChaseRange[] = [
  'close',
  'medium',
  'long',
  'distant',
  'extreme',
] as const;

const MANEUVER_LABEL: Record<ChaseManeuver, string> = {
  pursue: 'Pursue',
  flee: 'Flee',
  force: 'Force',
  quick_turn: 'Quick Turn',
  double_back: 'Double Back',
  trick: 'Trick',
};

const SAFETY_EASE_FACTOR: Record<ChaseManeuver, number> = {
  pursue: 6,
  flee: 6,
  force: 4,
  quick_turn: 4,
  double_back: 4,
  trick: 3,
};

const RANGE_LABEL: Record<ChaseRange, string> = {
  close: 'Close',
  medium: 'Medium',
  long: 'Long',
  distant: 'Distant',
  extreme: 'Extreme',
};

function rangeShiftDirection(maneuver: ChaseManeuver): 'closer' | 'farther' | 'none' {
  if (maneuver === 'pursue' || maneuver === 'force' || maneuver === 'double_back') {
    return 'closer';
  }
  if (maneuver === 'flee' || maneuver === 'quick_turn') {
    return 'farther';
  }
  return 'none';
}

export function ChaseSequenceCard({
  maneuver,
  bidEaseFactor,
  currentRange,
  rationale,
  heroPointsAvailable = 0,
  onDismiss,
  testidPrefix = 'br-chase-sequence',
}: ChaseSequenceCardProps) {
  const maneuverLabel = MANEUVER_LABEL[maneuver];
  const safetyEf = SAFETY_EASE_FACTOR[maneuver];
  const direction = rangeShiftDirection(maneuver);
  const currentRangeIdx = RANGE_ORDER.indexOf(currentRange);
  const bidBelowSafety = bidEaseFactor < safetyEf;
  const heroPointsHintVisible = heroPointsAvailable > 0;

  return (
    <aside
      className="br-chase-sequence-card"
      data-testid={testidPrefix}
      data-maneuver={maneuver}
      data-current-range={currentRange}
      data-bid-ef={bidEaseFactor}
      data-direction={direction}
    >
      <DispatchCardDismiss onDismiss={onDismiss} testidPrefix={testidPrefix} />
      <header className="br-chase-sequence-header">
        <span
          className="br-chase-sequence-maneuver"
          data-testid={`${testidPrefix}-maneuver`}
        >
          {maneuverLabel}
        </span>
        <span
          className="br-chase-sequence-bid br-label"
          data-testid={`${testidPrefix}-bid`}
        >
          Bid EF {bidEaseFactor}
        </span>
        <span
          className="br-chase-sequence-safety br-label"
          data-testid={`${testidPrefix}-safety`}
        >
          Safety EF {safetyEf}
        </span>
        {bidBelowSafety && (
          <span
            className="br-chase-sequence-mishap-warn"
            data-testid={`${testidPrefix}-mishap-warn`}
          >
            Mishap risk on failure
          </span>
        )}
      </header>

      <div
        className="br-chase-sequence-range-strip"
        data-testid={`${testidPrefix}-range-strip`}
        aria-label="Chase range strip"
      >
        {RANGE_ORDER.map((range, idx) => {
          const isCurrent = idx === currentRangeIdx;
          return (
            <span
              key={range}
              className="br-chase-sequence-range-cell"
              data-testid={`${testidPrefix}-range-${range}`}
              data-current={isCurrent ? 'true' : 'false'}
            >
              {RANGE_LABEL[range]}
            </span>
          );
        })}
      </div>

      {direction !== 'none' && (
        <p
          className="br-chase-sequence-direction"
          data-testid={`${testidPrefix}-direction-hint`}
        >
          {direction === 'closer'
            ? 'Closing the gap — range shifts toward Close on success.'
            : 'Opening the gap — range shifts toward Extreme on success.'}
        </p>
      )}

      {rationale !== undefined && rationale.length > 0 && (
        <p
          className="br-chase-sequence-rationale"
          data-testid={`${testidPrefix}-rationale`}
        >
          {rationale}
        </p>
      )}

      {heroPointsHintVisible && (
        <p
          className="br-chase-sequence-hero-points-hint"
          data-testid={`${testidPrefix}-hero-points-hint`}
          data-hero-points-available={heroPointsAvailable}
        >
          {heroPointsAvailable} Hero Point{heroPointsAvailable === 1 ? '' : 's'} available —
          spend to reroll bid or boost QR.
        </p>
      )}
    </aside>
  );
}
